import React, { useState, useEffect, useMemo, useRef, useCallback, createContext, useContext } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from 'react-toastify';
import { lazy } from 'react';
import Header from "./containers/Header/Header";
import Products from "./containers/Products/Products";
import Information from "./containers/Information/Information";
import GazUnloadSection from "./containers/Information/GazUnloadSection";
import Productions from "./containers/Productions/Productions";
import ProductionsNonmetallic from "./containers/Productions/ProductionsNonmetallic";
import Optimal from "./containers/Optimal/Optimal";
import Offer from "./containers/Offer/Offer";
import { getMskGazobetonScope } from "./utils/mskGazobetonScope";
import OfferNonmetallic from "./containers/Offer/OfferNonmetallic";
import Bitrix from "./containers/Bitrix/Bitrix";
import { AnalogsProvider } from "./AnalogsContext";
import { OptionsProvider, OptionsContext } from './OptionsContext';
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import LoginPage from "./LoginPage";
import { CircularProgress } from '@mui/material';
import ErrorBoundary from "./ErrorBoundary";
import { isCatSoundOn, playHappyChirp, playSadWhimper } from './utils/catMeow';
import PriceListsSummary from "./containers/PriceListsSummary/PriceListsSummary";
import WarehouseMurinoPrices from "./containers/WarehouseMurinoPrices/WarehouseMurinoPrices";
import ProductCatalog from "./containers/ProductCatalog/ProductCatalog";
import { AnalogsDataProvider } from './containers/Offer/AnalogsDataContext';
import NotificationStories from './components/NotificationStories';
import BellIntroNotification from './components/BellIntroNotification';
import { AnimatePresence, motion } from 'framer-motion';
import { CacheProvider } from "./projectProducts/CacheContext";
import { SearchResultsProvider } from "./hooks/SearchResultsContext";
import useCatClickEffect from "./hooks/useCatClickEffect";
import useHalloweenClickEffect from "./hooks/useHalloweenClickEffect";
import { useSeasonTheme, readCachedSeasonTheme, readSeasonThemeOn, writeSeasonThemeOn } from "./theme/seasonTheme";
import "./theme/redesign/tokens.css";
import "./theme/redesign/shell.css";
import "./theme/redesign/tooltips.css";
import "./theme/redesign/offer.css";
import "./theme/redesign/catgirl.css";
import "./theme/redesign/halloween.css";
import { scrollAppIntoView } from "./utils/pageScroll";
import { productNames, productNamesChanged } from "./utils/productNamesChanged";
const AdminPage = lazy(() => import("./pagesAdmin/AdminPage"));
const ServicePage = lazy(() => import("./projectProducts/ServicePage"));

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }

        fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/check-session`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setUser(data.user);
                    localStorage.setItem('user', JSON.stringify(data.user));
                } else {
                    setUser(null);
                    localStorage.removeItem('user');
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Ошибка проверки сессии:', err);
                setUser(null);
                localStorage.removeItem('user');
                setLoading(false);
                setError('Не удалось проверить сессию. Пожалуйста, войдите снова.');
            });
    }, []);

    const login = (userData) => {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const logout = () => {
        fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/logout`, {
            method: 'POST',
            credentials: 'include',
        })
            .then(() => {
                localStorage.removeItem('user');
                setUser(null);
            })
            .catch(err => console.error('Ошибка при выходе:', err));
    };

    return (
        <AuthContext.Provider value={{ user, role: user?.role, login, logout, loading, error }}>
            {children}
        </AuthContext.Provider>
    );
};

const REGIONS = {
    "СПБ": { latitude: 59.9342802, longitude: 30.3350986 },
    "МСК": { latitude: 55.755826, longitude: 37.6172999 },
    "КРД": { latitude: 45.035470, longitude: 38.975313 }
};

// ЛО используется только для самовывоза, не для доставки
const ALL_REGIONS = {
    "СПБ": { latitude: 59.9342802, longitude: 30.3350986 },
    "ЛО": { latitude: 60.051284, longitude: 30.438578 },
    "МСК": { latitude: 55.755826, longitude: 37.6172999 },
    "КРД": { latitude: 45.035470, longitude: 38.975313 }
};

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useContext(AuthContext);
    if (loading) {
        const SeasonLoader = readCachedSeasonTheme().Loader;
        if (SeasonLoader && readSeasonThemeOn()) return <SeasonLoader />;
        return <div style={{ width: "100vw", height: "100vh", display: 'flex', alignItems: "center", justifyContent: "center" }} > <CircularProgress size="7rem" /> </div>;
    }
    if (!user) {
        return <Navigate to="/login" />;
    }
    return children;
};



const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const dx = lat1 - lat2;
    const dy = lon1 - lon2;
    return Math.sqrt(dx * dx + dy * dy);
};

const findNearestRegion = (clientCoordinates) => {
    console.log("🌍 findNearestRegion вызвана с координатами:", clientCoordinates);

    if (!clientCoordinates || typeof clientCoordinates.latitude !== 'number' || typeof clientCoordinates.longitude !== 'number') {
        console.log("🌍 Некорректные координаты, возвращаем null");
        return null;
    }

    let nearestRegion = null;
    let minDistance = Infinity;

    Object.entries(REGIONS).forEach(([region, coords]) => {
        const distance = calculateDistance(
            clientCoordinates.latitude,
            clientCoordinates.longitude,
            coords.latitude,
            coords.longitude
        );
        console.log(`🌍 Регион ${region}: расстояние ${distance}`);
        if (distance < minDistance) {
            minDistance = distance;
            nearestRegion = region;
        }
    });

    console.log("🌍 Ближайший регион:", nearestRegion, "с расстоянием:", minDistance);
    return nearestRegion;
};

// Функция для проверки, содержит ли список продуктов только нерудку
const isOnlyNonmetallic = (products, options) => {
    if (!products || products.length === 0) return false;

    // Проверяем, есть ли валидные продукты
    const validProducts = products.filter(product =>
        product.name && product.name.trim() !== ""
    );

    if (validProducts.length === 0) return false;

    // Проверяем каждый продукт
    return validProducts.every(product => {
        const productOption = options[product.name];
        return productOption && productOption.CALCULATOR === "Нерудные материалы";
    });
};

const MainPage = ({
    gazCheapestHint,
    setGazCheapestHint,
    gazBrandHint,
    setGazBrandHint,
    selectGazProductionRef,
    onSelectGazProduction,
    clientName,
    setClientName,
    dealId,
    setDealId,
    locationFromDeal,
    setLocationFromDeal,
    selfPickUp,
    setSelfPickUp,
    selectedRegion,
    setSelectedRegion,
    deliveryDate,
    setDeliveryDate,
    selectedOffer,
    setSelectedOffer,
    offerRef,
    deliveryMethod,
    setDeliveryMethod,
    setDeliveryMethodFromOffer,
    paymentMethod,
    setPaymentMethod,
    addressAndCoordinates,
    setAddressAndCoordinates,
    products,
    setProducts,
    comparisons,
    setComparisons,
    excludedByPaymentMethod,
    setExcludedByPaymentMethod,
    nearestRegion,
    offerShouldReset,
    setOfferShouldReset,
    openModal, // Добавляем пропс
    closeModal, // Добавляем пропс
    productsRef, // Добавляем ref для Products
    setIsBitrix,
    onUpdateSelectedOffer,
    setGroupsDataApp,
    productFromCommercial,
    groupsDataApp,
    selectedProduction,
    showScrollButton,
    setShowScrollButton,
    gazOrderVariantMode,
    setGazOrderVariantMode,
}) => {
    const [data, setData] = useState([])
    const [isNonmetallicMode, setIsNonmetallicMode] = useState(false);
    // Сезонная тема: какая — решает сервер (SEASON_THEME), включена ли — пользователь
    const seasonTheme = useSeasonTheme();
    const { Overlay: SeasonThemeOverlay, Decorations: SeasonThemeDecorations } = seasonTheme;
    const [isSeasonThemeOn, setIsSeasonThemeOn] = useState(readSeasonThemeOn);
    const [themeTransition, setThemeTransition] = useState({ running: false, entering: false, origin: { x: 0, y: 0 } });
    const isCatTheme = isSeasonThemeOn && seasonTheme.id === 'cat';
    const isHalloweenTheme = isSeasonThemeOn && seasonTheme.id === 'halloween';

    useCatClickEffect(isCatTheme);
    useHalloweenClickEffect(isHalloweenTheme);

    // Синхронизируем класс на body — порталы (Bootstrap/MUI) рендерятся вне .app-redesign
    useEffect(() => {
        if (!isSeasonThemeOn) return undefined;
        const { bodyClass } = seasonTheme;
        document.body.classList.add(bodyClass);
        return () => document.body.classList.remove(bodyClass);
    }, [isSeasonThemeOn, seasonTheme]);

    // Звук на success/error тосты в кошко-теме — react-toastify рендерит
    // тосты в портал на body, поэтому ловим их через MutationObserver,
    // а не переписываем все 50+ мест вызова toast.success/toast.error
    useEffect(() => {
        if (!isCatTheme) return undefined;
        // .Toastify__toast-container монтируется лениво (только когда показан
        // первый тост), поэтому слушаем обёртку .Toastify — она есть всегда —
        // с subtree: true, а не ждём появления самого контейнера
        const root = document.querySelector('.Toastify') || document.body;

        const checkNode = (node) => {
            if (!(node instanceof HTMLElement)) return;
            if (node.matches('.Toastify__toast--success') || node.querySelector('.Toastify__toast--success')) {
                playHappyChirp();
            } else if (node.matches('.Toastify__toast--error') || node.querySelector('.Toastify__toast--error')) {
                playSadWhimper();
            }
        };

        const observer = new MutationObserver((mutations) => {
            if (!isCatSoundOn()) return;
            for (const mutation of mutations) {
                mutation.addedNodes.forEach(checkNode);
            }
        });
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [isCatTheme]);

    const toggleSeasonTheme = ({ x, y }) => {
        if (themeTransition.running) return;
        setThemeTransition({ running: true, entering: !isSeasonThemeOn, origin: { x, y } });
    };

    const handleTransitionMidpoint = () => {
        setIsSeasonThemeOn(prev => {
            const next = !prev;
            writeSeasonThemeOn(next);
            return next;
        });
    };

    const handleTransitionDone = () => {
        setThemeTransition(prev => ({ ...prev, running: false }));
    };
    const [nonmetallicDeliveryMethods, setNonmetallicDeliveryMethods] = useState({});
    const [addressValidationError, setAddressValidationError] = useState(null);
    const [productErrors, setProductErrors] = useState({});
    const { options } = useContext(OptionsContext) || { options: {} };
    const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

    // Разрешённая область разработки — калькулятор МСК → Газобетон. Признак считается
    // здесь: это единственный уровень, где одновременно доступны основные товары и
    // фактический регион расчёта. Ниже он передаётся готовым пропсом, компоненты его
    // не пересчитывают. Вне области всё поведение остаётся базовым.
    const calculationRegion = selfPickUp ? selectedRegion : nearestRegion;
    const isMskGazobetonScope = useMemo(
        () => getMskGazobetonScope({ products, region: calculationRegion }),
        [products, calculationRegion]
    );

    // Есть ли в заказе добавленные допродажи. Нужно только сравнению производств:
    // оно считается по строкам «Товаров», допродажи в него не попадают, и таблица
    // об этом честно оговаривается. Своего состояния не заводится — читается тот
    // же выбранный оффер, из которого допродажи и рисуются.
    const hasGazUpsells = isMskGazobetonScope
        && Array.isArray(selectedOffer?.materials)
        && selectedOffer.materials.some((material) => material?.isFromInsulationFriends);

    // Все производства с запрошенной номенклатурой отброшены по форме оплаты —
    // выбирать нечего, и расчёт заказа собирать не из чего. Сравнение при этом
    // честно показывает «регламент»: логист может найти условия или другого
    // поставщика. «Из справочника» за выбор не считается: это подстановка сервера
    // с нулевой ценой, по ней и рисуется «регламент».
    //
    // Одно производство может прийти несколькими источниками — в сообщении имя
    // должно быть названо один раз.
    const paymentUnavailableProductions = [...new Set(
        (excludedByPaymentMethod || [])
            .map((item) => item?.production)
            .filter(Boolean)
    )];
    // Выбрать можно только строку с ценой: производство с обнулённым (устаревшим)
    // прайсом уходит в «регламент» так же, как «Из справочника», и сообщение о форме
    // оплаты должно показываться и в этом случае. Условие повторяет validEntries из
    // useProductionsLogic, по которому работает автовыбор.
    const hasSelectableProduction = (comparisons || []).some(
        (comparison) => comparison?.production
            && comparison.production !== "Из справочника"
            && (comparison.materials || []).some((material) => Number(material?.pricePerOne) > 0)
    );
    const shouldExplainPaymentUnavailable =
        paymentUnavailableProductions.length > 0 && !hasSelectableProduction;

    // Сравнение пришло, но ни у одного производства нет цены — вся таблица ушла в
    // «регламент / Отправить запрос». КП собирать не из чего: блок не рисуется.
    // Пока сравнение грузится (список пуст), КП не прячется, чтобы не мигать на
    // каждом пересчёте. Нерудка считается своим сравнением — её не трогаем.
    const hasNoPricedProduction = !isNonmetallicMode
        && (comparisons || []).length > 0
        && !hasSelectableProduction;

    // КП из «Оптимального маршрута» собрано не из сравнения: маршрут набирает товары
    // у разных поставщиков, и сравнение (одно производство на весь заказ) в этом
    // случае как раз пустое. Прятать такое КП по пустому сравнению нельзя — иначе
    // «Выбрать» в маршруте ничего не показывает.
    const isOptimalRouteOffer = selectedOffer?.production === "Оптимальный маршрут";

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch(`${PATH_TO_BACKEND}/api/adminka/warehouse-stock-unique-all`);
            const data = await response.json();
            setData(data.data)
        };
        fetchData()
    }, [])

    // Отслеживаем изменения продуктов для переключения режима
    useEffect(() => {
        if (options && Object.keys(options).length > 0) {
            const nonmetallicMode = isOnlyNonmetallic(products, options);
            console.log("🔄 Проверка режима нерудки:", nonmetallicMode, "для продуктов:", products);
            if (nonmetallicMode !== isNonmetallicMode) {
                setIsNonmetallicMode(nonmetallicMode);
                console.log(nonmetallicMode ? "🟠 Переключились в режим нерудки" : "🔵 Переключились в обычный режим");

                // Сбрасываем выбранное предложение при переключении режима
                if (selectedOffer) {
                    setSelectedOffer(null);
                    setOfferShouldReset(true);
                }
            }
        }
    }, [products, options, isNonmetallicMode, selectedOffer, setSelectedOffer, setOfferShouldReset]);

    // Отдельный useEffect для обработки изменений формы оплаты
    useEffect(() => {
        console.log("🔄 useEffect paymentMethod triggered in MainPage, isNonmetallicMode:", isNonmetallicMode);
        // Изменение формы оплаты всегда должно скрывать предложение
        console.log("🔄 Setting offerShouldReset to true due to paymentMethod change");
        setOfferShouldReset(true);
    }, [paymentMethod, setOfferShouldReset]);

    // Статус для стикеров и реплик декораций темы — что сейчас на экране
    const themeStatus = (() => {
        const hasErrors = productErrors && Object.values(productErrors).some(Boolean);
        if (hasErrors) return 'error';
        const hasOffer = selectedOffer && selectedOffer.materials && selectedOffer.materials.length > 0;
        if (hasOffer) return 'offer';
        const hasProducts = Array.isArray(products) && products.some(p => p && p.name && p.name.trim() !== '');
        if (hasProducts) return 'counting';
        return 'empty';
    })();

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key="main-page"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={`app-redesign${isSeasonThemeOn ? ` ${seasonTheme.appClass}` : ''}`}
                style={{ minHeight: "100%", width: "100%", overflowX: "hidden" }}
            >
                <SeasonThemeOverlay
                    isRunning={themeTransition.running}
                    origin={themeTransition.origin}
                    entering={themeTransition.entering}
                    onMidpoint={handleTransitionMidpoint}
                    onDone={handleTransitionDone}
                />
                <Bitrix
                    setDealId={setDealId}
                    setClientName={setClientName}
                    setLocationFromDeal={setLocationFromDeal}
                    setSelfPickUp={setSelfPickUp}
                    setSelectedRegion={setSelectedRegion}
                    setDeliveryDate={setDeliveryDate}
                    setIsBitrix={setIsBitrix}
                />
                <BellIntroNotification />
                <NotificationStories />
                <div className="rd-shell">
                    {isSeasonThemeOn && <SeasonThemeDecorations status={themeStatus} />}
                    <Header products={products} seasonTheme={seasonTheme} isSeasonThemeOn={isSeasonThemeOn} toggleSeasonTheme={toggleSeasonTheme} />
                    <Information
                        delay={0.2}
                        selfPickUp={selfPickUp}
                        setSelfPickUp={setSelfPickUp}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        addressAndCoordinates={addressAndCoordinates}
                        setAddressAndCoordinates={setAddressAndCoordinates}
                        dealId={dealId}
                        locationFromDeal={locationFromDeal}
                        selectedRegion={selectedRegion}
                        setSelectedRegion={setSelectedRegion}
                        products={products}
                        addressValidationError={addressValidationError}
                        setAddressValidationError={setAddressValidationError}
                    />
                    <Products
                        ref={productsRef}
                        products={products}
                        setProducts={setProducts}
                        paymentMethod={paymentMethod}
                        region={selfPickUp ? selectedRegion : nearestRegion}
                        isMskGazobetonScope={isMskGazobetonScope}
                        setData={setData}
                        productErrors={productErrors}
                        setProductErrors={setProductErrors}
                        addressAndCoordinates={addressAndCoordinates}
                        selfPickUp={selfPickUp}
                        deliveryMethod={deliveryMethod}
                    />

                    {/* «Разгрузка на объекте» — отдельный блок между «Товарами» и расчётом
                        заказа. Разгрузка относится ко всему заказу и к доставке, а не к
                        строке товара, поэтому из «Товаров» выбор убран.
                        Условие ровно то же, что у остальной области, и другого места
                        выбора внутри неё нет: пока признак не включён, контрола нет ни
                        здесь, ни в «Товарах» — промежуточного показа в старом месте не
                        возникает. Своего состояния блок не заводит: значение и обработчик
                        читают и пишут то же поле deliveryMethod.gazUnloadMethod. */}
                    {isMskGazobetonScope && (
                        <GazUnloadSection
                            deliveryMethod={deliveryMethod}
                            setDeliveryMethod={setDeliveryMethod}
                        />
                    )}

                    {/* В МСК Газобетоне кнопки нет: КП там начинается со сводки и стоит
                        сразу под товарами. Условие стоит на рендере, а не на установке
                        showScrollButton, чтобы уже сохранённое значение не протекало в
                        область при переключении калькулятора без перезагрузки. Вне области
                        кнопка работает по базовому условию. */}
                    {showScrollButton && !isMskGazobetonScope && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                            <button
                                className="rd-scroll-to-offer-fab"
                                style={{ position: 'static' }}
                                onClick={() => {
                                    const target = offerRef?.current || document.querySelector('.rd-kp-section');
                                    if (target) {
                                        scrollAppIntoView(target);
                                    }
                                    setShowScrollButton(false);
                                }}
                                title="Перейти к КП"
                            >
                                КП ↓
                            </button>
                        </div>
                    )}

                    {shouldExplainPaymentUnavailable && !isOptimalRouteOffer && (
                        <div className="rd-section rd-offer-blocked">
                            <b>Расчёт заказа не сформирован.</b>{" "}
                            {paymentUnavailableProductions.length === 1
                                ? `У производства «${paymentUnavailableProductions[0]}» нет `
                                : `У производств ${paymentUnavailableProductions.map((name) => `«${name}»`).join(", ")} нет `}
                            {paymentMethod === "cash" ? "оплаты наличными" : "безналичной оплаты"}.
                            {" "}Смените форму оплаты или запросите поставщика по регламенту.
                        </div>
                    )}

                    {(!isOptimalRouteOffer && (shouldExplainPaymentUnavailable || hasNoPricedProduction)) || offerShouldReset || !selectedOffer || (selectedOffer && (!selectedOffer.materials || selectedOffer.materials.length === 0)) ? null : (
                        isNonmetallicMode ? (
                            <OfferNonmetallic
                                selectedOffer={selectedOffer}
                                ref={offerRef}
                                addressAndCoordinates={addressAndCoordinates}
                                dealId={dealId}
                                clientName={clientName}
                                onModalOpen={openModal}
                                onModalClose={closeModal}
                                data={data}
                                deliveryMethod={deliveryMethod}
                                setDeliveryMethod={setDeliveryMethodFromOffer}
                                setSelectedOffer={setSelectedOffer}
                                paymentMethod={paymentMethod}
                                nonmetallicDeliveryMethods={nonmetallicDeliveryMethods}
                                setNonmetallicDeliveryMethods={setNonmetallicDeliveryMethods}
                                productsRef={productsRef}
                                deliveryDate={deliveryDate}
                            />
                        ) : (
                            <Offer
                                selectedOffer={selectedOffer}
                                ref={offerRef}
                                addressAndCoordinates={addressAndCoordinates}
                                dealId={dealId}
                                clientName={clientName}
                                onModalOpen={openModal}
                                onModalClose={closeModal}
                                data={data}
                                deliveryMethod={deliveryMethod}
                                setDeliveryMethod={setDeliveryMethodFromOffer}
                                setSelectedOffer={setSelectedOffer}
                                paymentMethod={paymentMethod}
                                isMskGazobetonScope={isMskGazobetonScope}
                                deliveryDate={deliveryDate}
                                products={products}
                                gazCheapestHint={gazCheapestHint}
                                gazBrandHint={gazBrandHint}
                                onSelectGazProduction={onSelectGazProduction}
                                gazOrderVariantMode={gazOrderVariantMode}
                                setGazOrderVariantMode={setGazOrderVariantMode}
                            />
                        )
                    )}

                    {isNonmetallicMode ? (
                        <ProductionsNonmetallic
                            products={products}
                            paymentMethod={paymentMethod}
                            deliveryMethod={deliveryMethod}
                            setDeliveryMethod={setDeliveryMethod}
                            addressAndCoordinates={addressAndCoordinates}
                            selfPickUp={selfPickUp}
                            comparisons={comparisons}
                            setComparisons={setComparisons}
                            setSelectedOffer={setSelectedOffer}
                            offerRef={offerRef}
                            selectedRegion={selectedRegion}
                            nonmetallicDeliveryMethods={nonmetallicDeliveryMethods}
                            setNonmetallicDeliveryMethods={setNonmetallicDeliveryMethods}
                            addressValidationError={addressValidationError}
                            setAddressValidationError={setAddressValidationError}
                            setProductErrors={setProductErrors}
                        />
                    ) : (
                        <Productions
                            products={products}
                            setProducts={setProducts}
                            paymentMethod={paymentMethod}
                            deliveryMethod={deliveryMethod}
                            setDeliveryMethod={setDeliveryMethod}
                            addressAndCoordinates={addressAndCoordinates}
                            selfPickUp={selfPickUp}
                            comparisons={comparisons}
                            setComparisons={setComparisons}
                            setSelectedOffer={setSelectedOffer}
                            offerRef={offerRef}
                            selectedRegion={selfPickUp ? selectedRegion : nearestRegion}
                            isMskGazobetonScope={isMskGazobetonScope}
                            hasGazUpsells={hasGazUpsells}
                            setGazCheapestHint={setGazCheapestHint}
                            setGazBrandHint={setGazBrandHint}
                            selectProductionRef={selectGazProductionRef}
                            setGroupsDataApp={setGroupsDataApp}
                            productFromCommercial={productFromCommercial}
                            groupsDataApp={groupsDataApp}
                            selectedProduction={selectedProduction}
                            isNonmetallicMode={isNonmetallicMode}
                            dealId={dealId}
                            clientName={clientName}
                            deliveryDate={deliveryDate}
                            addressValidationError={addressValidationError}
                            setAddressValidationError={setAddressValidationError}
                            setShowScrollButton={setShowScrollButton}
                            productErrors={productErrors}
                            setProductErrors={setProductErrors}
                            setExcludedByPaymentMethod={setExcludedByPaymentMethod}
                            isCatMode={isCatTheme}
                        />
                    )}
                    <Optimal
                        products={products}
                        addressAndCoordinates={addressAndCoordinates}
                        comparisons={comparisons}
                        paymentMethod={paymentMethod}
                        setSelectedOffer={setSelectedOffer}
                        offerRef={offerRef}
                        selfPickUp={selfPickUp}
                        selectedOffer={selectedOffer}
                    />
                    <div style={{ height: "500px" }}></div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

function App() {
    const [clientName, setClientName] = useState("имя клиента");
    const [isBitrix, setIsBitrix] = useState(false)
    const [dealId, setDealId] = useState(null);
    const [locationFromDeal, setLocationFromDeal] = useState(null);
    const [selfPickUp, setSelfPickUp] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState("СПБ");
    const [deliveryDate, setDeliveryDate] = useState(""); // Дата поставки материала
    const [selectedOffer, setSelectedOffer] = useState(null);
    const offerRef = useRef(null);
    const productsRef = useRef(null);

    const [deliveryMethod, setDeliveryMethod] = useState({
        os: "Оптимальный маршрут",
        jbi: "Оптимальный маршрут",
        gaz: "Оптимальный маршрут",
        ner: "Оптимальный маршрут",
        mskDeliveryMode: "factory_to_client",
    });
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [addressAndCoordinates, setAddressAndCoordinates] = useState({
        address: "Санкт-Петербург",
        coordinates: { latitude: 59.9343, longitude: 30.3351 },
    });
    const [productFromCommercial, setProductsFromComercial] = useState(false)

    const [products, setProducts] = useState([
        {
            id: 1,
            category: "-",
            quantity: null,
            name: "",
            unit: "-",
            weight: "-",
            volume: "-",
            factVolume: "-",
            numberPallets: "-",
            itemsPerPallet: "-",
            source: "-"
        },
    ]);

    // Подсказка «есть вариант дешевле» считается там, где уже собрана таблица
    // сравнения (Productions), и только показывается в блоке расчёта. Своей цены
    // здесь не считается — хранится готовый результат.
    const [gazCheapestHint, setGazCheapestHint] = useState(null);
    // Подсказка «искали другой завод» приходит из сравнения производств, а
    // переключение обратно делает оно же: блок расчёта только показывает и
    // просит. Ссылка на переключатель живёт в ref, чтобы кнопка в расчёте не
    // перерисовывала таблицу сравнения на каждый рендер.
    const [gazBrandHint, setGazBrandHint] = useState(null);
    const gazSelectProductionRef = useRef(null);
    const handleSelectGazProduction = useCallback((comparisonId) => {
        if (typeof gazSelectProductionRef.current === 'function') {
            gazSelectProductionRef.current(comparisonId);
        }
    }, []);

    const [comparisons, setComparisons] = useState([
        {
            id: 1,
            production: "",
            materials: [
                { calculator: "", category: "", name: "", quantity: 0, unit: "", pricePerOne: 0, date: "", description: "", source: "" },
            ],
            productionInfo: { latitude: "", longitude: "", additionalInfo: "", inn: "", region: "" },
            delivery: {
                os: { distanceClientPoint: "", distanceProductionPoint: "", numberOfTrips: "", costPerTrip: "" },
                jbi: { distanceClientPoint: "", distanceProductionPoint: "", numberOfTrips: "", costPerTrip: "" },
                gaz: { distanceClientPoint: "", distanceProductionPoint: "", numberOfTrips: "", costPerTrip: "" },
                other: { distanceClientPoint: "", distanceProductionPoint: "", numberOfTrips: "", costPerTrip: "" },
            },
        },
    ]);

    const [nearestRegion, setNearestRegion] = useState(null);
    // Производства, отброшенные /api/filter по форме оплаты. Нужно, чтобы отличить
    // «товара нет ни у кого» от «у производства нет такой формы оплаты»: в первом
    // случае сравнение честно отправляет по регламенту, во втором расчёт заказа
    // собирать нельзя и менеджеру надо сказать, почему.
    const [excludedByPaymentMethod, setExcludedByPaymentMethod] = useState([]);
    const [offerShouldReset, setOfferShouldReset] = useState(false);
    // Названия строк с прошлой проверки: по ним видно, сменилась номенклатура или
    // правили количество. Первый прогон сравнивается с пустым списком и потому
    // ничего не сбрасывает — строки на старте и так пустые.
    const predydushchieImenaRef = useRef([]);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isDeliveryMethodChangingFromOffer, setIsDeliveryMethodChangingFromOffer] = useState(false);

    // Выбранный вариант заказа МСК Газобетона («весь заказ» / «только полностью
    // загруженные машины»). Живёт здесь, а не в блоке КП: смена способа разгрузки
    // меняет deliveryMethod, а это поднимает offerShouldReset и снимает весь блок КП
    // с монтирования — своё состояние он при этом теряет и возвращается к варианту по
    // умолчанию. Вариант заказа и способ разгрузки менеджер выбирает независимо,
    // поэтому выбор обязан пережить пересчёт.
    //
    // Второго состояния варианта это не заводит: значение по-прежнему одно, просто
    // хранится выше по дереву. Вне области оно не выставляется и не читается — там
    // сценарий КП живёт своим прежним состоянием в Offer.js.
    const [gazOrderVariantMode, setGazOrderVariantMode] = useState(null);

    const [modalOpenCount, setModalOpenCount] = useState(0);
    const [groupsDataApp, setGroupsDataApp] = useState([])
    const [productName, setProductionName] = useState(null)

    useEffect(() => {
        if (modalOpenCount > 0) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollBarWidth}px`;
            document.body.style.overflowX = "hidden";
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
            document.body.style.overflowX = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
            document.body.style.overflowX = "";
        };
    }, [modalOpenCount]);

    const openModal = () => {
        setModalOpenCount(prev => prev + 1);
    };

    const closeModal = () => {
        setModalOpenCount(prev => Math.max(prev - 1, 0));
    };

    // Wrapper для setDeliveryMethod, который используется в Offer компоненте
    const setDeliveryMethodFromOffer = (newDeliveryMethod) => {
        console.log("🔄 setDeliveryMethodFromOffer вызван с:", newDeliveryMethod);
        setIsDeliveryMethodChangingFromOffer(true);
        setDeliveryMethod(newDeliveryMethod);
    };

    useEffect(() => {
        console.log("🌍 App useEffect для nearestRegion, addressAndCoordinates:", addressAndCoordinates);
        if (addressAndCoordinates.address === "Париж, 31, улица Вице-адмирала Падорина, Североморск, ЗАТО Североморск, Мурманская область, Северо-Западный федеральный округ, 184606, Россия") {
            const region = "СПБ";
            console.log("🌍 Специальный адрес, устанавливаем регион:", region);
            setNearestRegion(region);
        } else {
            const region = findNearestRegion(addressAndCoordinates.coordinates);
            console.log("🌍 Найденный ближайший регион:", region, "для координат:", addressAndCoordinates.coordinates);
            setNearestRegion(region);
        }
    }, [addressAndCoordinates]);

    useEffect(() => {
        console.log("🔄 useEffect deliveryMethod triggered, isDeliveryMethodChangingFromOffer:", isDeliveryMethodChangingFromOffer, "deliveryMethod:", deliveryMethod);
        // Не скрываем Offer если изменение deliveryMethod произошло из-за выбора альтернативной доставки
        if (!isDeliveryMethodChangingFromOffer) {
            console.log("🔄 Setting offerShouldReset to true");
            setOfferShouldReset(true);
        } else {
            console.log("🔄 Skipping offerShouldReset because change is from Offer");
        }
    }, [
        selfPickUp,
        deliveryMethod,
        addressAndCoordinates,
        dealId,
        locationFromDeal,
    ]);


    // Отдельный useEffect для сброса флага isDeliveryMethodChangingFromOffer
    useEffect(() => {
        if (isDeliveryMethodChangingFromOffer) {
            console.log("🔄 Resetting isDeliveryMethodChangingFromOffer flag");
            const timer = setTimeout(() => {
                setIsDeliveryMethodChangingFromOffer(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isDeliveryMethodChangingFromOffer]);

    // Изменились строки товаров — расчёт заказа больше не соответствует вводу и
    // прячется до пересборки. Условие `!selectedOffer` здесь стояло раньше, и из-за
    // него механизм не работал ровно в том случае, ради которого написан: пока КП
    // существует, оно и должно спрятаться. Показывает его обратно эффект ниже —
    // когда производство переприменится на новых данных.
    useEffect(() => {
        // Игнорируем временные изменения (с флагом _isTemporary)
        const hasTemporaryChanges = products.some(product => product._isTemporary);

        // Название строки — исключение из этой отсрочки. Выбор номенклатуры тоже
        // помечается временным на секунду debounce, и за эту секунду расчёт мог
        // переприменить производство на прежних данных: на экране оставался
        // расчёт по старому товару (второй сорт), хотя в строке уже выбран другой
        // (20.10.2026, Роман). Поэтому смену названия обрабатываем сразу и
        // снимаем выбранное предложение — пересоберётся на новых сравнениях.
        const imenaSeychas = productNames(products);
        const imenaSmenilis = productNamesChanged(predydushchieImenaRef.current, imenaSeychas);
        predydushchieImenaRef.current = imenaSeychas;

        if (imenaSmenilis && !isDeliveryMethodChangingFromOffer) {
            setOfferShouldReset(true);
            setSelectedOffer(null);
            return;
        }

        if (!hasTemporaryChanges && !isDeliveryMethodChangingFromOffer) {
            setOfferShouldReset(true);
        }
    }, [JSON.stringify(products), isDeliveryMethodChangingFromOffer]);

    useEffect(() => {
        if (!isDeliveryMethodChangingFromOffer) {
            setOfferShouldReset(true);
        }
    }, [JSON.stringify(comparisons), isDeliveryMethodChangingFromOffer]);

    // Расчёт заказа показывается заново, когда выбрано предложение — то есть когда
    // оно пересобрано на актуальных сравнениях.
    //
    // `offerShouldReset` в зависимостях здесь быть не должно: с ним эффект
    // срабатывал на собственный флаг и, пока предложение выбрано, немедленно
    // возвращал его в false. Из-за этого КП не прятался ни при смене формы оплаты,
    // ни при смене количества или добавлении номенклатуры — оставался на экране
    // собранным по прежним данным.
    useEffect(() => {
        if (selectedOffer) {
            setOfferShouldReset(false);
        } else {
            setShowScrollButton(false);
        }
    }, [selectedOffer]);

    return (
        <AuthProvider>
            <OptionsProvider>
                <AnalogsDataProvider>
                    <AnalogsProvider>
                        <CacheProvider>
                            <SearchResultsProvider>
                                <Router>
                                    <ErrorBoundary>
                                        <Routes>
                                            <Route path="/login" element={<LoginPage />} />
                                            <Route
                                                path="/"
                                                element={
                                                    <MainPage
                                                        clientName={clientName}
                                                        setClientName={setClientName}
                                                        dealId={dealId}
                                                        setDealId={setDealId}
                                                        locationFromDeal={locationFromDeal}
                                                        setLocationFromDeal={setLocationFromDeal}
                                                        selfPickUp={selfPickUp}
                                                        setSelfPickUp={setSelfPickUp}
                                                        selectedRegion={selectedRegion}
                                                        setSelectedRegion={setSelectedRegion}
                                                        deliveryDate={deliveryDate}
                                                        setDeliveryDate={setDeliveryDate}
                                                        selectedOffer={selectedOffer}
                                                        setSelectedOffer={setSelectedOffer}
                                                        offerRef={offerRef}
                                                        deliveryMethod={deliveryMethod}
                                                        setDeliveryMethod={setDeliveryMethod}
                                                        setDeliveryMethodFromOffer={setDeliveryMethodFromOffer}
                                                        paymentMethod={paymentMethod}
                                                        setPaymentMethod={setPaymentMethod}
                                                        addressAndCoordinates={addressAndCoordinates}
                                                        setAddressAndCoordinates={setAddressAndCoordinates}
                                                        products={products}
                                                        setProducts={setProducts}
                                                        comparisons={comparisons}
                                                        excludedByPaymentMethod={excludedByPaymentMethod}
                                                        setExcludedByPaymentMethod={setExcludedByPaymentMethod}
                                                        gazCheapestHint={gazCheapestHint}
                                                        setGazCheapestHint={setGazCheapestHint}
                                                        gazBrandHint={gazBrandHint}
                                                        setGazBrandHint={setGazBrandHint}
                                                        selectGazProductionRef={gazSelectProductionRef}
                                                        onSelectGazProduction={handleSelectGazProduction}
                                                        setComparisons={setComparisons}
                                                        nearestRegion={(() => {
                                                            const result = selfPickUp ? selectedRegion : nearestRegion;
                                                            console.log(`🌍 Передаем в Information nearestRegion: ${result}, selfPickUp: ${selfPickUp}, selectedRegion: ${selectedRegion}, nearestRegion: ${nearestRegion}`);
                                                            return result;
                                                        })()}
                                                        offerShouldReset={offerShouldReset}
                                                        setOfferShouldReset={setOfferShouldReset}
                                                        openModal={openModal}
                                                        closeModal={closeModal}
                                                        productsRef={productsRef}
                                                        setIsBitrix={setIsBitrix}
                                                        setGroupsDataApp={setGroupsDataApp}
                                                        productFromCommercial={productFromCommercial}
                                                        groupsDataApp={groupsDataApp}
                                                        selectedProduction={productName}
                                                        showScrollButton={showScrollButton}
                                                        setShowScrollButton={setShowScrollButton}
                                                        gazOrderVariantMode={gazOrderVariantMode}
                                                        setGazOrderVariantMode={setGazOrderVariantMode}
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/price-lists"
                                                element={<PriceListsSummary isBitrix={isBitrix} setIsBitrix={setIsBitrix} />}
                                            />
                                            <Route
                                                path="/warehouse-murino"
                                                element={<WarehouseMurinoPrices />}
                                            />
                                            <Route
                                                path="/service"
                                                element={<ServicePage />}
                                            />
                                            <Route
                                                path="/catalog"
                                                element={<ProductCatalog />}
                                            />
                                            <Route
                                                path="/adminka/*"
                                                element={<ProtectedRoute><AdminPage /></ProtectedRoute>}
                                            />
                                        </Routes>
                                        <ToastContainer
                                            position="top-right"
                                            autoClose={3000}
                                            hideProgressBar={false}
                                            newestOnTop
                                            closeOnClick
                                            rtl={false}
                                            pauseOnFocusLoss
                                            draggable
                                            pauseOnHover
                                        />
                                    </ErrorBoundary>
                                </Router>
                            </SearchResultsProvider>
                        </CacheProvider>
                    </AnalogsProvider>
                </AnalogsDataProvider>
            </OptionsProvider>
        </AuthProvider>
    );
}

export default App;
